/* Click positions for the admin heatmap.
 *
 * What is sent: a page path, a cell in a coarse grid, the visible text of
 * whatever was clicked, a desktop/mobile class, and who clicked.
 *
 * Who means one of two things and never both. A signed-in visitor sends a
 * Firebase ID token, which the server checks and turns into the account's
 * email -- the email itself is never sent, because a payload can claim to be
 * anyone. Everybody else sends a random id that lives as long as the browsing
 * session, so a run of clicks can be read as one person without anybody being
 * identified. No IP address is sent or stored: the privacy policy promises no
 * historical IP addresses are kept, and this keeps that true.
 *
 * Recording happens only while an admin has the switch on in Settings, and the
 * flag is read from the pricing response every visitor already fetches, so a
 * site with the feature off makes no extra request and installs no listener.
 *
 * A tracker must never be why a click did not work, so every step here fails
 * silently and the listener is passive.
 */
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { getPricingJson } from "./pricingFeed";

const BASE = "https://us-central1-veyra-200d7.cloudfunctions.net/v2Api";
const TRACK_API = `${BASE}/v2TrackClicks`;

const FLAG_KEY = "veyra:heatmap";
const VISITOR_KEY = "veyra:heatmap:visitor";

/* A label for one visit by someone not signed in, so a handful of clicks can
   be read as one person rather than five strangers.

   sessionStorage, not localStorage: an id that survives across visits is a
   tracking cookie by another name, and the point of this over an IP was to
   answer the question without keeping something that identifies a person. It
   is random, it is meaningless on its own, and it is gone when the tab is. */
function visitorId(): string | null {
  try {
    const existing = sessionStorage.getItem(VISITOR_KEY);
    if (existing) return existing;
    const source =
      globalThis.crypto?.randomUUID?.() ??
      Math.random().toString(36) + Math.random().toString(36);
    const id = source.replace(/[^a-z0-9]/g, "").slice(0, 8) || "anon0000";
    sessionStorage.setItem(VISITOR_KEY, id);
    return id;
  } catch {
    /* Private mode: the visit simply goes unlabelled. */
    return null;
  }
}

/* How long an answer about the switch is trusted before asking again.

   This used to be cached for the whole session with no expiry, so a tab opened
   before the switch was turned on kept "off" until it was closed -- turning the
   feature on recorded nothing from anyone already browsing, which is precisely
   what made it look dead on arrival.

   A minute is short enough that flipping the switch takes effect while someone
   is still looking, and long enough that the re-check is normally served from
   the HTTP cache instead of the network. The pricing response is cacheable for
   five minutes, so that is the real upper bound on propagation. */
const FLAG_TTL_MS = 60000;

/** Must match V2_HEATMAP_COLUMNS / ROWS in the functions source. */
const COLUMNS = 50;
const ROWS = 100;

const MAX_BUFFER = 40;
const FLUSH_MS = 15000;

/* Must match V2_HEATMAP_PATHS in the functions source; anything else is dropped
   there. Every page of the site rather than the handful it started with -- a
   heatmap that covers the landing page and nothing customers do afterwards
   answers the least interesting half of the question.

   Still a list rather than "record whatever path arrives": the path becomes
   part of a document id, so an open-ended one is a way for anyone to write
   junk into the collection. Routes carrying an id in the URL (invoices) and
   the one-shot payment callbacks are left out on purpose -- unbounded ids, and
   nothing worth reading from a page nobody looks at twice. */
const TRACKED_PATHS = new Set([
  "/",
  "/products",
  "/product",
  "/features",
  "/pricing",
  "/downloads",
  "/redeem",
  "/profile",
  "/login",
  "/signup",
  "/lua-docs",
  "/media-team",
  "/moderator",
  "/terms",
  "/privacy",
]);

/* Product pages moved from /product?game=<id> to /product/<id>. They are
   one layout, recorded under "/product" as before -- the functions side only
   accepts the paths in its own list, and the heatmap overlay shows the page
   as one template anyway. */
function trackedPath(path: string): string {
  return path.startsWith("/product/") ? "/product" : path;
}

interface ClickEvent {
  x: number;
  y: number;
  label: string;
}

/** Honoured before anything else: asked not to be measured, so we do not. */
function optedOut(): boolean {
  const nav = navigator as Navigator & {
    globalPrivacyControl?: boolean;
    msDoNotTrack?: string;
  };
  return (
    nav.doNotTrack === "1" ||
    nav.msDoNotTrack === "1" ||
    nav.globalPrivacyControl === true ||
    (window as unknown as { doNotTrack?: string }).doNotTrack === "1"
  );
}

async function heatmapEnabled(): Promise<boolean> {
  try {
    const raw = sessionStorage.getItem(FLAG_KEY);
    if (raw) {
      const cached = JSON.parse(raw) as { on?: boolean; at?: number };
      if (typeof cached.at === "number" && Date.now() - cached.at < FLAG_TTL_MS) {
        return cached.on === true;
      }
    }
  } catch {
    /* Private mode, or a value written by an older build. Ask again. */
  }

  try {
    /* The same public, CDN-cached response the homepage and product page
       already fetch (utils/pricingFeed), so this adds no request of its own.
       It used to fetch it separately, from the function URL. */
    const data = await getPricingJson();
    if (!data) return false;
    const on = data.settings?.heatmapEnabled === true;
    try {
      sessionStorage.setItem(FLAG_KEY, JSON.stringify({ on, at: Date.now() }));
    } catch {
      /* ignore */
    }
    return on;
  } catch {
    return false;
  }
}

/* innerText follows what is actually rendered, so sibling blocks come back
   separated. textContent runs them together: a product card whose badge, title
   and tagline are separate elements reads as "LiveDeadlock External + Internal
   Our most..." with no spaces at the joins. */
const visibleText = (el: Element): string =>
  ((el as HTMLElement).innerText || el.textContent || "")
    .replace(/\s+/g, " ")
    .trim();

/** What was clicked, named the way the person clicking would name it. */
function labelFor(target: EventTarget | null): string {
  const el = target instanceof Element ? target : null;

  /* closest(), not a capped walk up a fixed number of levels. A product card
     wraps its whole body in one link and the title inside it sits deeper than
     a few elements, so a capped walk gave up before reaching the link and
     labelled the most interesting click on the page as nothing at all.
     Anything genuinely outside a control still returns "", as before. */
  const clickable =
    el?.closest("a, button, [role='button'], input[type='submit']") ?? null;
  if (!clickable) return "";

  const aria = clickable.getAttribute("aria-label");
  if (aria) return aria.replace(/\s+/g, " ").trim().slice(0, 40);

  /* Whole-card links are one control wrapping a badge, a title, a tagline and
     a price, so the control's own text is the entire card. The element under
     the pointer is the specific thing that was aimed at, and is usually the
     short, meaningful one -- the title, or the button's own words. */
  if (target instanceof Element) {
    const own = visibleText(target);
    if (own && own.length <= 40) return own;
  }

  const heading = clickable.querySelector("h1, h2, h3, h4, h5, h6");
  if (heading) {
    const headingText = visibleText(heading);
    if (headingText) return headingText.slice(0, 40);
  }

  return visibleText(clickable).slice(0, 40);
}

/* The admin heatmap draws its overlay on top of the real page, loaded in an
   iframe. Recording in there would count looking at the numbers as new
   numbers, and every visit to the heatmap would add a click to whatever the
   admin's cursor happened to be over. A cross-origin frame throws on the
   comparison, which is also a frame, so that answers true as well. */
function framed(): boolean {
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
}

export function startHeatmap(): () => void {
  if (typeof window === "undefined" || optedOut() || framed()) return () => {};

  let buffer: ClickEvent[] = [];
  /* The path the buffered clicks happened on, not the one showing when they are
     sent -- otherwise a click on the homepage flushed after a navigation would
     be counted against whatever page the visitor moved to. */
  let bufferPath = window.location.pathname;
  let live = true;
  let installed = false;
  let flushTimer: number | undefined;

  /* Kept current in the background because send() has to stay synchronous --
     it runs from pagehide, where there is no time to await a token, and the
     clicks worth having are the last ones before someone leaves. */
  let idToken: string | null = null;
  const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
    if (!user) {
      idToken = null;
      return;
    }
    user
      .getIdToken()
      .then((token) => {
        idToken = token;
      })
      .catch(() => {
        idToken = null;
      });
  });

  const send = () => {
    const events = buffer;
    const path = trackedPath(bufferPath);
    buffer = [];
    if (events.length === 0 || !TRACKED_PATHS.has(path)) return;

    const payload = JSON.stringify({
      path,
      device: window.innerWidth < 768 ? "mobile" : "desktop",
      events,
      /* One or the other. The token proves who this is; the visitor id claims
         nothing and only groups a single visit together. */
      ...(idToken ? { token: idToken } : { visitor: visitorId() }),
    });

    try {
      const blob = new Blob([payload], { type: "text/plain;charset=UTF-8" });
      if (!navigator.sendBeacon?.(TRACK_API, blob)) {
        void fetch(TRACK_API, {
          method: "POST",
          body: payload,
          keepalive: true,
          headers: { "Content-Type": "text/plain;charset=UTF-8" },
        }).catch(() => {});
      }
    } catch {
      /* Never let recording be the reason anything breaks. */
    }
  };

  const onClick = (event: MouseEvent) => {
    if (!live) return;

    const path = window.location.pathname;
    if (path !== bufferPath) {
      send();
      bufferPath = path;
    }
    if (!TRACKED_PATHS.has(trackedPath(path))) return;

    const doc = document.documentElement;
    const width = Math.max(doc.scrollWidth, 1);
    const height = Math.max(doc.scrollHeight, 1);
    const x = Math.floor((event.pageX / width) * COLUMNS);
    const y = Math.floor((event.pageY / height) * ROWS);
    if (x < 0 || x >= COLUMNS || y < 0 || y >= ROWS) return;

    buffer.push({ x, y, label: labelFor(event.target) });
    if (buffer.length >= MAX_BUFFER) send();
  };

  const onVisibility = () => {
    if (document.visibilityState === "hidden") send();
  };

  const install = () => {
    if (installed) return;
    installed = true;
    document.addEventListener("click", onClick, { capture: true, passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", send);
    flushTimer = window.setInterval(send, FLUSH_MS);
  };

  const uninstall = () => {
    if (!installed) return;
    installed = false;
    document.removeEventListener("click", onClick, { capture: true });
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", send);
    if (flushTimer) window.clearInterval(flushTimer);
    flushTimer = undefined;
    /* Switched off means nothing is recorded, including whatever is still
       buffered from a moment ago. */
    buffer = [];
  };

  /* Asked again on a timer rather than settled once at startup. Someone
     browsing when the switch is turned on starts being counted without having
     to open a new tab, and -- the half that matters more -- someone browsing
     when it is turned off stops being counted without one either. */
  const poll = async () => {
    if (!live) return;
    const on = await heatmapEnabled();
    if (!live) return;
    if (on) install();
    else uninstall();
  };

  void poll();
  const flagTimer = window.setInterval(() => {
    void poll();
  }, FLAG_TTL_MS);

  return () => {
    live = false;
    if (installed) send();
    uninstall();
    window.clearInterval(flagTimer);
    unsubscribeAuth();
  };
}
